import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

// Sheets can stack (e.g. a picker plus an inline "create" sheet), so the body
// scroll lock is reference-counted: only the last sheet to close releases it.
let scrollLocks = 0;

function lockBodyScroll() {
  if (scrollLocks++ === 0) document.body.style.overflow = 'hidden';
}

function unlockBodyScroll() {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks === 0) document.body.style.overflow = '';
}

/**
 * Modal sheet that slides up from the bottom on mobile and renders as a
 * centred dialog from the `md` breakpoint up.
 */
function BottomSheet({ open, onClose, title, children, footer, fullHeight = false }) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current?.(); };
    document.addEventListener('keydown', onKey);
    lockBodyScroll();
    return () => {
      document.removeEventListener('keydown', onKey);
      unlockBodyScroll();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/40 animate-fade-in"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={`w-full md:max-w-lg bg-white rounded-t-2xl md:rounded-2xl shadow-xl flex flex-col animate-sheet-up md:animate-fade-in ${
          fullHeight ? 'h-[92dvh] md:h-auto md:max-h-[85vh]' : 'max-h-[88dvh] md:max-h-[85vh]'
        }`}
      >
        <div className="md:hidden flex justify-center pt-2">
          <div className="w-10 h-1.5 rounded-full bg-gray-300" />
        </div>
        {title && (
          <div className="flex items-center justify-between px-5 pt-3 pb-2">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="-mr-2 p-2 rounded-full text-gray-500 hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-5 pb-4">{children}</div>
        {footer && <div className="border-t border-gray-100 px-5 pt-3 pb-safe">{footer}</div>}
        {!footer && <div className="pb-safe" />}
      </div>
    </div>,
    document.body
  );
}

export default BottomSheet;
